import { useCallback, useState } from 'react';
import addHours from 'date-fns/addHours';
import nextMonday from 'date-fns/nextMonday';
import startOfDay from 'date-fns/startOfDay';
import startOfTomorrow from 'date-fns/startOfTomorrow';

import { useTranslation } from '@lyearn/i18n';

import {
  getAssignmentNumberLabel,
  getCustomOption,
  getScheduledForOption,
  NO_SCHEDULE_DATE,
  ScheduleForEnum,
  ScheduleItem,
} from '../../utils';

export default function useAssignmentHeader({
  assignmentNumber,
  onAssign,
}: {
  assignmentNumber?: number;
  onAssign: (value: ScheduleForEnum, item: ScheduleItem) => void;
}) {
  const { t } = useTranslation();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerError, setDatePickerError] = useState<
    'ScheduleDateEmptyError' | 'ScheduleDateError'
  >();
  const items = getScheduledForOption(t);
  const onMenuChange = useCallback(
    (value: string, item: ScheduleItem) => {
      const selectedValue = value as ScheduleForEnum;
      switch (selectedValue) {
        case ScheduleForEnum.Custom:
          setShowDatePicker(true);
          break;
        case ScheduleForEnum.MondayAt9:
          onAssign(selectedValue, {
            ...item,
            value: {
              scheduleDateType: item.value.scheduleDateType,
              scheduleDate: addHours(startOfDay(nextMonday(new Date())), 9).toISOString(),
            },
          });
          break;
        case ScheduleForEnum.TomorrowAt9:
          onAssign(selectedValue, {
            ...item,
            value: {
              scheduleDateType: item.value.scheduleDateType,
              scheduleDate: addHours(startOfTomorrow(), 9).toISOString(),
            },
          });
          break;
        case ScheduleForEnum.After7Days:
          onAssign(selectedValue, item);
          break;
        default:
          onAssign(selectedValue, item);
      }
    },
    [onAssign],
  );

  const onCustomDateSelect = useCallback(
    (selectedDate: any) => {
      if (selectedDate) {
        const customValue = getCustomOption(t);
        onAssign(ScheduleForEnum.Custom, {
          ...customValue,
          value: {
            scheduleDateType: customValue.value.scheduleDateType,
            scheduleDate: (selectedDate as Date)?.toISOString(),
          },
        });
        setShowDatePicker(false);
        setDatePickerError(undefined);
      } else {
        setDatePickerError('ScheduleDateEmptyError');
      }
    },
    [onAssign, t],
  );

  const onDirectAssign = useCallback(() => {
    onAssign(ScheduleForEnum.NoScheduleDate, NO_SCHEDULE_DATE);
  }, [onAssign]);

  const closeDatePicker = useCallback(() => setShowDatePicker(false), []);

  const assignmentNumberLabel = getAssignmentNumberLabel(assignmentNumber);

  return {
    data: {
      assignmentNumberLabel,
      actionMenuItems: items,
      showDatePicker,
      datePickerError,
    },
    methods: {
      onMenuChange,
      onDirectAssign,
      onCustomDateSelect,
      closeDatePicker,
    },
  };
}
